import {
  loginAll as sdkBridgeLoginAll,
  configure as configureSdkBridge
} from '../../governanceBridge.js'
import {
  isTrustedHandoffTarget,
  encodeHandoffPayload,
  appendHandoffFragment
} from '../../functions/authHandoff.js'

// Editor runs on SDK auth for its core surfaces. Governance + financials
// are both optional extensions that activate only when the user opens
// something that needs them (chat embed, grants page) OR when their
// claims match the extension's shouldActivate predicate (admin role,
// team-org membership). Default editor user — personal org, no team
// membership — triggers zero bridge calls at login.
configureSdkBridge({
  required: [],
  optional: ['governance', 'financials']
})

// Fan out to the Supabase federation after the Symbols SDK login
// succeeds. Governance (+ any enabled extensions) hydrate their own
// sessions so the editor can embed chat/meet/tickets UIs without a
// second auth step. Non-blocking and best-effort — if a bridge is
// unreachable, the editor still proceeds; the federated sessions are
// additive, not required.
const _fanOutBridgeLogin = async (email, password) => {
  try {
    const result = await sdkBridgeLoginAll({ email, password })
    if (!result.ok) {
      console.warn('[editor/signin] bridge login returned error:', result.error?.message)
    } else {
      const failed = result.results.filter(r => r.status === 'error')
      if (failed.length) {
        for (const r of failed) console.warn(`[editor/signin] bridge ${r.key} failed:`, r.error?.message)
      }
    }
  } catch (err) {
    console.warn('[editor/signin] bridge login threw:', err?.message || err)
  }
}

export const signin = {
  extends: 'Page',
  metadata: { title: 'Sign in / Symbols' },
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  width: '100%',
  minHeight: '100dvh',
  maxWidth: 'none',

  SigninCard: {
    extends: 'AppLoginCard',
    eyebrow: 'Sign in',
    title: 'Welcome',
    sub: 'Use your email or username and password.',

    Form: {
      // Override email placeholder + name so the identifier field accepts
      // username or email (Symbols SDK accepts both).
      Email: { placeholder: 'Email or username', name: 'identifier', autocomplete: 'username' },

      // Replace the Supabase-direct submit with SDK login + bridge federation.
      onSubmit: async (ev, el, s) => {
        ev.preventDefault()
        const root = el.node
        const identifier = (root.querySelector('input[name="identifier"]')?.value || '').trim().toLowerCase()
        const password = root.querySelector('input[name="password"]')?.value || ''
        const errBox = root.querySelector('[data-lg-err]')
        if (errBox) errBox.textContent = ''
        if (!identifier || !password) {
          if (errBox) errBox.textContent = 'Email and password are required.'
          return
        }
        try {
          const pluginSession = el.call('getPluginSessionFromUrl')
          const loginResult = pluginSession
            ? await el.sdk.login(identifier, password, { session: pluginSession })
            : await el.sdk.login(identifier, password)
          el.call('applyLoader', { value: true, force: true, timeout: 1 })
          const resolvedEmail = loginResult?.user?.email || identifier
          // Await federation BEFORE the redirect — otherwise the page
          // navigates away mid-flight and the governance Supabase JWT
          // never gets persisted (matches AppLoginCard pattern).
          await _fanOutBridgeLogin(resolvedEmail, password)
          await el.call('initializeUserSession', { loginData: loginResult })

          // Cross-origin handoff: when `?redirect=<previewUrl>` is set
          // AND the target is a trusted preview host, mint a fragment
          // handoff and replace location instead of routing into the
          // dashboard. This is what makes `https://<x>.preview.symbols.app/...`
          // bounce through `my.symbols.app/signin` and come back signed-in.
          const redirectParam = (typeof window !== 'undefined')
            ? new URL(window.location.href).searchParams.get('redirect')
            : null
          if (redirectParam && isTrustedHandoffTarget(redirectParam)) {
            // SDK login returns `response.data` with `{ tokens: {accessToken,
            // refreshToken, accessTokenExp: {expiresIn}} }`. Fall back to
            // `getAuthToken()` if the shape changes — TokenManager has the
            // freshly-persisted token regardless.
            const tokens = loginResult?.tokens || loginResult?.data?.tokens || {}
            const accessToken = tokens.accessToken
              || tokens.access_token
              || (typeof el.sdk.getAuthToken === 'function' ? el.sdk.getAuthToken() : null)
            const refreshToken = tokens.refreshToken || tokens.refresh_token || ''
            const expiresIn = tokens.accessTokenExp?.expiresIn || tokens.expiresIn || tokens.expires_in || 0
            const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : 0
            if (accessToken) {
              const payload = encodeHandoffPayload({ accessToken, refreshToken, expiresAt, expiresIn })
              const target = appendHandoffFragment(redirectParam, payload)
              window.location.replace(target)
              return
            }
          }

          el.router('/dashboard', el.getRoot())
        } catch (error) {
          console.error('Failed to sign in:', error)
          if (errBox) errBox.textContent = error.message || 'Sign-in failed.'
          el.call('openNotification', {
            title: 'Failed to sign in',
            message: error.message,
            type: 'error'
          })
        }
        el.call('applyLoader', { value: false, force: true, timeout: 1 })
      }
    }
  }
}
